import { normalizeRutgersBaseCourseId } from './courseIdFormatter';

export type AcademicPaletteSwatch = {
  id: string;
  label: string;
  solid: string;
  soft: string;
  border: string;
  text: string;
  contrastText: string;
};

export type AcademicPalettePreset = {
  id: string;
  name: string;
  description: string;
  courseSwatches: AcademicPaletteSwatch[];
  programSwatches: AcademicPaletteSwatch[];
  isCustom?: boolean;
};

export type AcademicPalettePreferences = {
  coursePaletteId: string;
  programPaletteId: string;
};

const makeSwatch = (
  id: string,
  label: string,
  solid: string,
  soft: string,
  border: string,
  text: string,
  contrastText = '#ffffff'
): AcademicPaletteSwatch => ({
  id,
  label,
  solid,
  soft,
  border,
  text,
  contrastText,
});

function normalizeHexColor(value: string): string {
  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#64748b';
}

function hexToRgb(hex: string) {
  const normalized = normalizeHexColor(hex).replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function toRgba(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function relativeLuminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function deriveReadableText(hex: string) {
  return relativeLuminance(hex) > 0.46 ? '#1f2937' : hex;
}

function deriveContrastText(hex: string) {
  return relativeLuminance(hex) > 0.56 ? '#111827' : '#ffffff';
}

function slugify(value: string) {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'palette';
}

export function createCustomAcademicPaletteSwatch(
  id: string,
  label: string,
  solid: string
): AcademicPaletteSwatch {
  const normalized = normalizeHexColor(solid);
  return makeSwatch(
    slugify(id),
    label.trim() || 'Custom',
    normalized,
    toRgba(normalized, 0.14),
    toRgba(normalized, 0.30),
    deriveReadableText(normalized),
    deriveContrastText(normalized)
  );
}

export function createCustomAcademicPalettePreset(input: {
  name: string;
  description?: string;
  courseSwatches: Array<{ label: string; solid: string }>;
  programSwatches: Array<{ label: string; solid: string }>;
}): AcademicPalettePreset {
  const paletteSlug = slugify(input.name);
  const fallbackDescription = 'Custom palette stored on this device.';
  return {
    id: `custom-${paletteSlug}-${Date.now()}`,
    name: input.name.trim() || 'Custom Palette',
    description: input.description?.trim() || fallbackDescription,
    courseSwatches: input.courseSwatches.map((swatch, index) =>
      createCustomAcademicPaletteSwatch(
        `${paletteSlug}-course-${index + 1}`,
        swatch.label,
        swatch.solid
      )
    ),
    programSwatches: input.programSwatches.map((swatch, index) =>
      createCustomAcademicPaletteSwatch(
        `${paletteSlug}-program-${index + 1}`,
        swatch.label,
        swatch.solid
      )
    ),
    isCustom: true,
  };
}

export const ACADEMIC_PALETTE_PRESETS: AcademicPalettePreset[] = [
  {
    id: 'scarlet-slate',
    name: 'Scarlet Slate',
    description: 'Rutgers-forward reds and grounded cool tones.',
    courseSwatches: [
      makeSwatch('scarlet', 'Scarlet', '#c81e3a', 'rgba(200,30,58,0.12)', 'rgba(200,30,58,0.34)', '#9f1239'),
      makeSwatch('brick', 'Brick', '#b45309', 'rgba(180,83,9,0.12)', 'rgba(180,83,9,0.34)', '#9a3412'),
      makeSwatch('plum', 'Plum', '#7c3aed', 'rgba(124,58,237,0.12)', 'rgba(124,58,237,0.34)', '#6d28d9'),
      makeSwatch('ocean', 'Ocean', '#2563eb', 'rgba(37,99,235,0.12)', 'rgba(37,99,235,0.34)', '#1d4ed8'),
      makeSwatch('forest', 'Forest', '#0f766e', 'rgba(15,118,110,0.12)', 'rgba(15,118,110,0.34)', '#0f766e'),
      makeSwatch('graphite', 'Graphite', '#475569', 'rgba(71,85,105,0.12)', 'rgba(71,85,105,0.34)', '#334155'),
    ],
    programSwatches: [
      makeSwatch('garnet', 'Garnet', '#be123c', 'rgba(190,18,60,0.12)', 'rgba(190,18,60,0.34)', '#9f1239'),
      makeSwatch('ruby', 'Ruby', '#dc2626', 'rgba(220,38,38,0.12)', 'rgba(220,38,38,0.34)', '#b91c1c'),
      makeSwatch('amber', 'Amber', '#d97706', 'rgba(217,119,6,0.12)', 'rgba(217,119,6,0.34)', '#b45309'),
      makeSwatch('cobalt', 'Cobalt', '#1d4ed8', 'rgba(29,78,216,0.12)', 'rgba(29,78,216,0.34)', '#1e40af'),
      makeSwatch('violet', 'Violet', '#7c3aed', 'rgba(124,58,237,0.12)', 'rgba(124,58,237,0.34)', '#6d28d9'),
      makeSwatch('teal', 'Teal', '#0f766e', 'rgba(15,118,110,0.12)', 'rgba(15,118,110,0.34)', '#115e59'),
    ],
  },
  {
    id: 'studio-neon',
    name: 'Studio Neon',
    description: 'Sharper, brighter color separation for dense planners.',
    courseSwatches: [
      makeSwatch('electric-blue', 'Electric Blue', '#0284c7', 'rgba(2,132,199,0.12)', 'rgba(2,132,199,0.34)', '#0369a1'),
      makeSwatch('magenta', 'Magenta', '#db2777', 'rgba(219,39,119,0.12)', 'rgba(219,39,119,0.34)', '#be185d'),
      makeSwatch('lime', 'Lime', '#65a30d', 'rgba(101,163,13,0.12)', 'rgba(101,163,13,0.34)', '#4d7c0f'),
      makeSwatch('orange', 'Orange', '#ea580c', 'rgba(234,88,12,0.12)', 'rgba(234,88,12,0.34)', '#c2410c'),
      makeSwatch('indigo', 'Indigo', '#4f46e5', 'rgba(79,70,229,0.12)', 'rgba(79,70,229,0.34)', '#4338ca'),
      makeSwatch('cyan', 'Cyan', '#0891b2', 'rgba(8,145,178,0.12)', 'rgba(8,145,178,0.34)', '#0e7490'),
    ],
    programSwatches: [
      makeSwatch('rose', 'Rose', '#e11d48', 'rgba(225,29,72,0.12)', 'rgba(225,29,72,0.34)', '#be123c'),
      makeSwatch('blue', 'Blue', '#2563eb', 'rgba(37,99,235,0.12)', 'rgba(37,99,235,0.34)', '#1d4ed8'),
      makeSwatch('emerald', 'Emerald', '#059669', 'rgba(5,150,105,0.12)', 'rgba(5,150,105,0.34)', '#047857'),
      makeSwatch('violet', 'Violet', '#7c3aed', 'rgba(124,58,237,0.12)', 'rgba(124,58,237,0.34)', '#6d28d9'),
      makeSwatch('amber', 'Amber', '#d97706', 'rgba(217,119,6,0.12)', 'rgba(217,119,6,0.34)', '#b45309'),
      makeSwatch('sky', 'Sky', '#0ea5e9', 'rgba(14,165,233,0.12)', 'rgba(14,165,233,0.34)', '#0284c7'),
    ],
  },
  {
    id: 'earth-notebook',
    name: 'Earth Notebook',
    description: 'Warmer, softer colors with less visual strain.',
    courseSwatches: [
      makeSwatch('clay', 'Clay', '#b45309', 'rgba(180,83,9,0.12)', 'rgba(180,83,9,0.34)', '#9a3412'),
      makeSwatch('moss', 'Moss', '#4d7c0f', 'rgba(77,124,15,0.12)', 'rgba(77,124,15,0.34)', '#3f6212'),
      makeSwatch('denim', 'Denim', '#1d4ed8', 'rgba(29,78,216,0.12)', 'rgba(29,78,216,0.34)', '#1e40af'),
      makeSwatch('berry', 'Berry', '#be185d', 'rgba(190,24,93,0.12)', 'rgba(190,24,93,0.34)', '#9d174d'),
      makeSwatch('teal', 'Teal', '#0f766e', 'rgba(15,118,110,0.12)', 'rgba(15,118,110,0.34)', '#115e59'),
      makeSwatch('umber', 'Umber', '#7c2d12', 'rgba(124,45,18,0.12)', 'rgba(124,45,18,0.34)', '#7c2d12'),
    ],
    programSwatches: [
      makeSwatch('garnet', 'Garnet', '#9f1239', 'rgba(159,18,57,0.12)', 'rgba(159,18,57,0.34)', '#881337'),
      makeSwatch('copper', 'Copper', '#c2410c', 'rgba(194,65,12,0.12)', 'rgba(194,65,12,0.34)', '#9a3412'),
      makeSwatch('olive', 'Olive', '#4d7c0f', 'rgba(77,124,15,0.12)', 'rgba(77,124,15,0.34)', '#3f6212'),
      makeSwatch('navy', 'Navy', '#1e3a8a', 'rgba(30,58,138,0.12)', 'rgba(30,58,138,0.34)', '#1e3a8a'),
      makeSwatch('mulberry', 'Mulberry', '#7e22ce', 'rgba(126,34,206,0.12)', 'rgba(126,34,206,0.34)', '#6b21a8'),
      makeSwatch('pine', 'Pine', '#166534', 'rgba(22,101,52,0.12)', 'rgba(22,101,52,0.34)', '#166534'),
    ],
  },
  {
    id: 'pastel-lab',
    name: 'Pastel Lab',
    description: 'Airier chip colors for lighter dashboards and profile views.',
    courseSwatches: [
      makeSwatch('coral', 'Coral', '#e11d48', 'rgba(244,114,182,0.14)', 'rgba(225,29,72,0.28)', '#be123c'),
      makeSwatch('sun', 'Sun', '#d97706', 'rgba(251,191,36,0.16)', 'rgba(217,119,6,0.28)', '#b45309'),
      makeSwatch('mint', 'Mint', '#059669', 'rgba(52,211,153,0.14)', 'rgba(5,150,105,0.28)', '#047857'),
      makeSwatch('sky', 'Sky', '#0284c7', 'rgba(56,189,248,0.14)', 'rgba(2,132,199,0.28)', '#0369a1'),
      makeSwatch('lilac', 'Lilac', '#7c3aed', 'rgba(167,139,250,0.14)', 'rgba(124,58,237,0.28)', '#6d28d9'),
      makeSwatch('slate', 'Slate', '#64748b', 'rgba(148,163,184,0.14)', 'rgba(100,116,139,0.28)', '#475569'),
    ],
    programSwatches: [
      makeSwatch('rose', 'Rose', '#db2777', 'rgba(244,114,182,0.14)', 'rgba(219,39,119,0.28)', '#be185d'),
      makeSwatch('amber', 'Amber', '#d97706', 'rgba(251,191,36,0.16)', 'rgba(217,119,6,0.28)', '#b45309'),
      makeSwatch('seafoam', 'Seafoam', '#0f766e', 'rgba(45,212,191,0.14)', 'rgba(15,118,110,0.28)', '#115e59'),
      makeSwatch('azure', 'Azure', '#2563eb', 'rgba(96,165,250,0.14)', 'rgba(37,99,235,0.28)', '#1d4ed8'),
      makeSwatch('iris', 'Iris', '#7c3aed', 'rgba(167,139,250,0.14)', 'rgba(124,58,237,0.28)', '#6d28d9'),
      makeSwatch('stone', 'Stone', '#57534e', 'rgba(168,162,158,0.14)', 'rgba(87,83,78,0.28)', '#44403c'),
    ],
  },
];

export const DEFAULT_ACADEMIC_PALETTE_PREFERENCES: AcademicPalettePreferences = {
  coursePaletteId: ACADEMIC_PALETTE_PRESETS[0].id,
  programPaletteId: ACADEMIC_PALETTE_PRESETS[1].id,
};

export function getAcademicPalettePreset(
  id: string | undefined | null,
  presets: AcademicPalettePreset[] = ACADEMIC_PALETTE_PRESETS
): AcademicPalettePreset {
  return (
    presets.find((preset) => preset.id === id) ??
    presets[0] ??
    ACADEMIC_PALETTE_PRESETS[0]
  );
}

export function hashText(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = value.charCodeAt(index) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getAcademicSwatch(
  text: string,
  paletteId: string | undefined | null,
  kind: 'course' | 'program',
  presets: AcademicPalettePreset[] = ACADEMIC_PALETTE_PRESETS
): AcademicPaletteSwatch {
  const preset = getAcademicPalettePreset(paletteId, presets);
  const swatches = kind === 'course' ? preset.courseSwatches : preset.programSwatches;
  const normalized = text.trim().toLowerCase();
  const index = hashText(normalized) % swatches.length;
  return swatches[index];
}

export type CourseAcademicSwatchResult =
  | { kind: 'accent'; normalizedBaseId: string; swatch: AcademicPaletteSwatch }
  | { kind: 'neutral'; normalizedBaseId: null; swatch: null };

export function getCourseAcademicSwatch(
  courseId: string | null | undefined,
  paletteId: string | undefined | null,
  presets: AcademicPalettePreset[] = ACADEMIC_PALETTE_PRESETS
): CourseAcademicSwatchResult {
  const normalizedBaseId = normalizeRutgersBaseCourseId(courseId);
  if (!normalizedBaseId) return { kind: 'neutral', normalizedBaseId: null, swatch: null };
  return {
    kind: 'accent',
    normalizedBaseId,
    swatch: getAcademicSwatch(normalizedBaseId, paletteId, 'course', presets),
  };
}

/** Explicit department identity for views whose grouping unit is a department. */
export function getDepartmentAcademicSwatch(
  department: string,
  paletteId: string | undefined | null,
  presets: AcademicPalettePreset[] = ACADEMIC_PALETTE_PRESETS
): AcademicPaletteSwatch {
  return getAcademicSwatch(department.trim().toLowerCase(), paletteId, 'course', presets);
}

export function buildAcademicBadgeStyle(swatch: AcademicPaletteSwatch) {
  return {
    backgroundColor: swatch.soft,
    borderColor: swatch.border,
    color: swatch.text,
  };
}

export function buildAcademicSolidStyle(swatch: AcademicPaletteSwatch) {
  return {
    backgroundColor: swatch.solid,
    color: swatch.contrastText,
  };
}
